import { type PartialPropsOf } from '@blaze-runtime/engine-domain';

import {
  applyParentStyleOverrideRules,
  applySimpleAlignParentContentOverrides,
  type ParentStyleOverrideRule,
} from '../../utils/elements/props-builder.utils';
import { PropsBuilder } from '../__base/props-builder';
import {
  type FormChildrenContentOverrides,
  type FormChildrenStyleOverrides,
} from '../form-container/form-container.model';

const SCORE_FORM_CHILD_STYLE_CLEAR: PartialPropsOf<'score'> = {
  style: {
    general: { title: undefined, accent: undefined },
    value: {
      default: { color: undefined, fill: undefined, outline: undefined },
      hover: { color: undefined, fill: undefined, outline: undefined },
      selected: { color: undefined, fill: undefined, outline: undefined },
    },
  },
};

function getScoreFormChildStyleRules(
  overrides: FormChildrenStyleOverrides,
): ParentStyleOverrideRule<'score'>[] {
  const { titleLabel, fieldLabel, fieldBox } = overrides;

  return [
    {
      value: titleLabel?.color,
      path: 'style.general.title',
      patch: { style: { general: { title: titleLabel?.color } } },
    },
    {
      value: fieldBox?.accent,
      path: 'style.general.accent',
      patch: { style: { general: { accent: fieldBox?.accent } } },
    },
    {
      value: fieldLabel?.color,
      path: 'style.value.default.color',
      patch: { style: { value: { default: { color: fieldLabel?.color } } } },
    },
    {
      value: fieldBox?.fill,
      path: 'style.value.default.fill',
      patch: { style: { value: { default: { fill: fieldBox?.fill } } } },
    },
    {
      value: fieldBox?.outline,
      path: 'style.value.default.outline',
      patch: { style: { value: { default: { outline: fieldBox?.outline } } } },
    },
    {
      value: fieldBox?.accent,
      path: 'style.value.selected.fill',
      patch: { style: { value: { selected: { fill: fieldBox?.accent } } } },
    },
  ];
}

export class ScorePropsBuilder extends PropsBuilder<'score'> {
  override setMargin = (margin: string | undefined): this => {
    this.patchProps({ style: { general: { offset: { margin } } } });
    return this;
  };

  override setParentContentProps = (
    elementProps: PartialPropsOf<'score'>,
    overrides: FormChildrenContentOverrides | null,
    initialValues?: PartialPropsOf<'score'>,
  ): this => {
    applySimpleAlignParentContentOverrides(
      (patch) => this.patchProps(patch),
      elementProps,
      overrides,
      initialValues,
    );
    return this;
  };

  override setParentStyleProps = (
    elementProps: PartialPropsOf<'score'>,
    overrides: FormChildrenStyleOverrides | null,
    initialValues?: PartialPropsOf<'score'>,
  ): this => {
    applyParentStyleOverrideRules(
      (patch) => this.patchProps(patch),
      elementProps,
      overrides,
      initialValues,
      SCORE_FORM_CHILD_STYLE_CLEAR,
      overrides ? getScoreFormChildStyleRules(overrides) : [],
    );
    return this;
  };

  override setReadonly = (readonly: boolean): this => {
    this.patchProps({ content: { options: { readonly } } });
    return this;
  };

  constructor(props: PartialPropsOf<'score'> | undefined) {
    super(props, { hasData: true, hasLabel: true });
  }
}
